import { useState, useEffect } from 'react'
import { X, Trash2, Users, Building2, User } from 'lucide-react'
import { filesApi, departmentApi, serverApi, type FilesShare } from '../../services/api'
import { dialog } from '../../services/dialog'
import { useActiveServer } from '../../hooks/useActiveServer'

interface Props {
  itemKind: 'folder' | 'object'
  itemId: string
  itemName: string
  onClose: () => void
}

type TargetType = 'company' | 'department' | 'user'

interface Department {
  id: string
  name: string
}

interface Member {
  userId: string
  user: { id: string; email: string; firstName?: string; lastName?: string; avatarUrl: string | null }
}

export default function ShareDialog({ itemKind, itemId, itemName, onClose }: Props) {
  const server = useActiveServer()
  const serverId = server?.id ?? ''

  const [shares, setShares] = useState<FilesShare[]>([])
  const [departments, setDepartments] = useState<Department[]>([])
  const [members, setMembers] = useState<Member[]>([])
  const [loading, setLoading] = useState(false)

  // form state
  const [targetType, setTargetType] = useState<TargetType>('company')
  const [targetDeptId, setTargetDeptId] = useState('')
  const [targetUserId, setTargetUserId] = useState('')
  const [perm, setPerm] = useState<'view' | 'edit'>('view')
  const [expiresAt, setExpiresAt] = useState('')

  useEffect(() => {
    // Load existing shares, departments, members in parallel
    Promise.all([
      filesApi.shares(),
      serverId ? departmentApi.list(serverId) : Promise.resolve({ data: [] }),
      serverId ? serverApi.members(serverId) : Promise.resolve({ data: [] }),
    ]).then(([sharesRes, deptsRes, membersRes]) => {
      const allShares = sharesRes.data.shares
      setShares(
        allShares.filter((s) =>
          itemKind === 'folder' ? s.folderId === itemId : s.objectId === itemId
        )
      )
      setDepartments((deptsRes.data as Department[]) ?? [])
      setMembers((membersRes.data as Member[]) ?? [])
    }).catch(() => {
      setShares([])
    })
  }, [itemId, itemKind, serverId])

  const handleShare = async () => {
    if (targetType === 'department' && !targetDeptId) {
      await dialog.alert({ title: 'Select a department', tone: 'default' })
      return
    }
    if (targetType === 'user' && !targetUserId) {
      await dialog.alert({ title: 'Select a user', tone: 'default' })
      return
    }
    setLoading(true)
    try {
      const body: Parameters<typeof filesApi.share>[0] = {
        targetType,
        perm,
        expiresAt: expiresAt || undefined,
      }
      if (itemKind === 'folder') body.folderId = itemId
      else body.objectId = itemId
      if (targetType === 'department') body.targetDepartmentId = targetDeptId
      if (targetType === 'user') body.targetUserId = targetUserId

      const { data: shareRes } = await filesApi.share(body)
      setShares((prev) => [...prev, shareRes.share])
      // Reset form
      setTargetType('company')
      setTargetDeptId('')
      setTargetUserId('')
      setPerm('view')
      setExpiresAt('')
    } catch {
      await dialog.alert({ title: 'Failed to share', tone: 'destructive' })
    } finally {
      setLoading(false)
    }
  }

  const handleRevoke = async (shareId: string) => {
    const ok = await dialog.confirm({
      title: 'Revoke access',
      message: 'Remove this share? The recipient will lose access.',
      tone: 'destructive',
      confirmLabel: 'Revoke',
    })
    if (!ok) return
    try {
      await filesApi.revokeShare(shareId)
      setShares((prev) => prev.filter((s) => s.id !== shareId))
    } catch {
      await dialog.alert({ title: 'Failed to revoke share', tone: 'destructive' })
    }
  }

  const targetLabel = (share: FilesShare) => {
    if (share.targetType === 'company') return 'Whole company'
    if (share.targetType === 'department') {
      const dept = departments.find((d) => d.id === share.targetDepartmentId)
      return dept ? `Dept: ${dept.name}` : `Dept: ${share.targetDepartmentId}`
    }
    const member = members.find((m) => m.userId === share.targetUserId)
    if (member) {
      const name = [member.user.firstName, member.user.lastName].filter(Boolean).join(' ')
      return name || member.user.email
    }
    return share.targetUserId ?? 'Unknown user'
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="font-semibold text-foreground text-base">Share "{itemName}"</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Target type */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Share with</label>
            <div className="flex gap-2">
              {(
                [
                  { val: 'company' as TargetType, label: 'Company', icon: Building2 },
                  { val: 'department' as TargetType, label: 'Department', icon: Users },
                  { val: 'user' as TargetType, label: 'User', icon: User },
                ] as const
              ).map(({ val, label, icon: Icon }) => (
                <button
                  key={val}
                  onClick={() => setTargetType(val)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border transition ${
                    targetType === val
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-border text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon size={14} />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Department picker */}
          {targetType === 'department' && (
            <div className="space-y-1">
              <label className="text-sm font-medium text-foreground">Department</label>
              <select
                value={targetDeptId}
                onChange={(e) => setTargetDeptId(e.target.value)}
                className="w-full h-9 px-3 text-sm bg-muted border border-border rounded-md text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="">Select department…</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* User picker */}
          {targetType === 'user' && (
            <div className="space-y-1">
              <label className="text-sm font-medium text-foreground">User</label>
              <select
                value={targetUserId}
                onChange={(e) => setTargetUserId(e.target.value)}
                className="w-full h-9 px-3 text-sm bg-muted border border-border rounded-md text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="">Select user…</option>
                {members.map((m) => {
                  const name = [m.user.firstName, m.user.lastName].filter(Boolean).join(' ')
                  return (
                    <option key={m.userId} value={m.userId}>
                      {name || m.user.email}
                    </option>
                  )
                })}
              </select>
            </div>
          )}

          {/* Permission */}
          <div className="flex gap-4 items-center">
            <label className="text-sm font-medium text-foreground shrink-0">Permission</label>
            <div className="flex gap-2">
              {(['view', 'edit'] as const).map((p) => (
                <button
                  key={p}
                  onClick={() => setPerm(p)}
                  className={`px-3 py-1.5 rounded-md text-sm border capitalize transition ${
                    perm === p
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-border text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          {/* Expiry */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-foreground">
              Expires at <span className="text-muted-foreground font-normal">(optional)</span>
            </label>
            <input
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="w-full h-9 px-3 text-sm bg-muted border border-border rounded-md text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Add share */}
          <button
            onClick={handleShare}
            disabled={loading}
            className="w-full h-9 bg-primary text-primary-foreground text-sm font-medium rounded-md hover:opacity-90 transition disabled:opacity-50"
          >
            {loading ? 'Sharing…' : 'Share'}
          </button>

          {/* Existing shares */}
          {shares.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">Current access</p>
              <div className="space-y-1">
                {shares.map((share) => (
                  <div
                    key={share.id}
                    className="flex items-center justify-between bg-muted px-3 py-2 rounded-md"
                  >
                    <div className="min-w-0">
                      <p className="text-sm text-foreground truncate">{targetLabel(share)}</p>
                      <p className="text-xs text-muted-foreground capitalize">
                        {share.perm}
                        {share.expiresAt ? ` · expires ${new Date(share.expiresAt).toLocaleDateString()}` : ''}
                      </p>
                    </div>
                    <button
                      onClick={() => handleRevoke(share.id)}
                      className="ml-3 shrink-0 text-muted-foreground hover:text-destructive transition"
                      title="Revoke"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
